import i18n from '../../i18n';
import { client } from '../../gql/client';
import { preferServerWithCacheFallback } from '../../gql/cacheFallback';
import { GET_ORDER_BY_ID } from '../../gql/queries/orders/orders';
import { warmupOrdersByIds } from '../../gql/selectors/warmupOrders';
import { warmupCommunicationsByIds } from '../../gql/selectors/warmupCommunications';
import { warmupAccidentsByIds } from '../../gql/selectors/warmupAccidents';
import { GET_CAMPAIGN_BY_ID } from '../../gql/queries/campaign';
import { GET_TEAM_BY_ID } from '../../gql/queries/users';
import {
  CHUNK_SIZE,
  safeQuery,
  formatRoid,
  formatDate,
  translateState,
  fetchImages,
  fetchActionRecord,
  fetchAccidentRecord,
  fetchCommunicationRecord,
  resolveLocationFields,
  resolveClassificationFields,
  resolveRegisteredByName,
  resolveCreatorFields,
} from './fetchReportData';
import { CombinedReportData, CombinedEntityType } from './combinedTypes';
import { DetailedPDFRecord } from './detailedTypes';
import { OrderType } from '../../types/orders';
import { isWorkState } from '../../constants/workStates';
import {
  buildReportChildrenSequence,
  ReportSortOption,
  DEFAULT_REPORT_SORT,
} from './combinedSort';

/**
 * Hija del informe + fechas crudas de su Order (para ordenar). El
 * `DetailedPDFRecord` solo lleva fechas formateadas para pintar, por eso se
 * conservan aquí las originales de la Order hija.
 */
type ChildWithDates = {
  record: DetailedPDFRecord;
  registerInitDate?: string | null;
  creationDate?: string | null;
};

function t(key: string, defaultValue?: string): string {
  return i18n.t(key, { defaultValue: defaultValue ?? key });
}

function formatGeneratedAt(): string {
  const localeMap: Record<string, string> = {
    ca: 'ca-ES',
    es: 'es-ES',
    en: 'en-GB',
  };
  const dateLocale = localeMap[i18n.language] || 'ca-ES';
  return new Date().toLocaleDateString(dateLocale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Equipos del expediente padre (`order.teamIDs` → "Nom1, Nom2"). Solo tiene
 * sentido en el PADRE: en las hijas (actuación/accidente/comunicación) el
 * `teamIDs` está denormalizado pero no significa nada — pintarlo allí sería
 * mentir. Ver invariante 2 de specs/backend/campaigns-generic.md.
 */
async function resolveTeamFields(
  teamIDs: string[] = []
): Promise<{ label: string; value: string }[]> {
  if (!teamIDs.length) return [];

  const names = await Promise.all(
    teamIDs.map(async (id) => {
      const data = await safeQuery<{ team: any }>(GET_TEAM_BY_ID, { id });
      return data?.team?.name ?? '';
    })
  );
  const value = names.filter(Boolean).join(', ');
  if (!value) return [];

  return [{ label: t('forms:workOrder.team', 'Equip'), value }];
}

/**
 * Cuánto se espera al servidor por cada Order del informe antes de caer a la
 * caché: 8 s, no el plazo compartido de las pantallas
 * (`CACHE_FALLBACK_DEADLINE_MS`, 2 s).
 *
 * Un informe es un documento entregable: prima acertar el estado sobre la
 * rapidez. Con 2 s, un servidor lento haría que una hija saliera de la caché y
 * el filtro de borradores y anuladas decidiera con un estado viejo. Las
 * pantallas siguen con el plazo compartido: allí hay alguien mirando la rueda.
 * Sin red o con el servidor caído no se espera nada: el fallo es inmediato y
 * se cae a la caché al momento.
 */
export const REPORT_SERVER_DEADLINE_MS = 8000;

/**
 * La Order del SERVIDOR primero, con la caché de respaldo (decisión D3 y
 * revisión de #347). Mismo patrón que la validación de actuaciones
 * (`preferServerWithCacheFallback`):
 *
 * - Con red: la del servidor. El informe decide qué actuaciones entran por su
 *   ESTADO, y con la caché persistida de Apollo una que ya pasó de «Parcial» a
 *   «Abierta» se quedaría fuera por un estado viejo; y la lista de hijas del
 *   padre dejaría fuera las creadas después de cachearlo (medido en el RV el
 *   22-sep: 13 hijas en el servidor y 7 en caché).
 * - Sin red, con el servidor caído o si tarda más de
 *   `REPORT_SERVER_DEADLINE_MS`: la de la caché, como antes de D3, y el
 *   informe se genera igual. 🔴 Ahí el estado puede estar DESFASADO: el filtro
 *   de borradores y anuladas se aplica con lo que diga la caché.
 * - Ni red ni caché: `null`, lo mismo que devolvía antes `safeQuery`.
 */
async function readOrderPreferServer(
  id: string
): Promise<{ order: any } | null> {
  try {
    return await preferServerWithCacheFallback<{ order: any } | null>(
      client
        .query<{ order: any }>({
          query: GET_ORDER_BY_ID,
          variables: { id },
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => data ?? null),
      () =>
        client.readQuery<{ order: any }>({
          query: GET_ORDER_BY_ID,
          variables: { id },
        }),
      REPORT_SERVER_DEADLINE_MS
    );
  } catch {
    return null;
  }
}

/**
 * Resuelve las hijas de una Order padre (`childExpeditionOrderIDs`) en
 * chunks, despachando cada id por tipo a los builders del informe detallado.
 * Las actuaciones en borrador o anuladas se quedan fuera (`isWorkState`), y
 * el progreso las cuenta igual, porque también se han leído. Devuelve UNA
 * sola lista mixta con las fechas CRUDAS de cada Order hija — el orden final
 * (hilo cronológico entrelazado) lo pone `buildReportChildrenSequence`.
 */
async function fetchChildrenWithDates(
  childOrderIds: string[],
  cloudName: string,
  onProgress?: (current: number, total: number) => void
): Promise<ChildWithDates[]> {
  const children: ChildWithDates[] = [];
  let completed = 0;

  // Warm-up #92 aplicado al informe combinado (misma clase que el bonus de
  // export-modals de #93): UNA llamada OrdersByIds puebla la caché para los
  // ids de comunicaciones y accidentes de abajo. Se queda `cache-first`, como
  // estaba: no decide qué hijas entran (eso lo hace la lectura per-row, del
  // servidor primero) y a red no tendría plazo — con mala cobertura colgaría
  // el informe, que hoy sale de la caché sin esperar.
  await warmupOrdersByIds(childOrderIds);

  // Warm-ups hermanos (Tier C/D): las Communications y los Accidents de las
  // hijas en UNA query batch por tipo (`communicationsByIds` /
  // `accidentsByIds`) — los GET_COMMUNICATION_BY_ID per-row de
  // `fetchCommunicationRecord` y los GET_ACCIDENT_BY_ID per-row de
  // `fetchAccidentRecord` (cache-first vía safeQuery) pasan a hit por los
  // redirects Query.communication / Query.accident. Los ids salen de la
  // caché recién calentada (readQuery, cero red); si el warm-up de Orders
  // falló, readQuery devuelve null y esto se salta solo — per-row como
  // fallback.
  try {
    const commIds: (string | undefined)[] = [];
    const accidentIds: (string | undefined)[] = [];
    childOrderIds.forEach((childId) => {
      const cached: {
        order?: { communicationIDs?: string[]; accidentIDs?: string[] } | null;
      } | null = client.readQuery({
        query: GET_ORDER_BY_ID,
        variables: { id: childId },
      });
      commIds.push(cached?.order?.communicationIDs?.[0]);
      accidentIds.push(cached?.order?.accidentIDs?.[0]);
    });
    await Promise.all([
      warmupCommunicationsByIds(commIds),
      warmupAccidentsByIds(accidentIds),
    ]);
  } catch {
    // Warm-up opcional: sin efectos si falla.
  }

  for (let i = 0; i < childOrderIds.length; i += CHUNK_SIZE) {
    const chunk = childOrderIds.slice(i, i + CHUNK_SIZE);
    const chunkRecords = await Promise.all(
      chunk.map(async (childId): Promise<ChildWithDates | null> => {
        const childRes = await readOrderPreferServer(childId);
        const childOrder = childRes?.order;
        const childType = childOrder?.type;
        let rec: DetailedPDFRecord | null = null;
        if (childType === OrderType.Action) {
          // Una actuación en borrador o anulada no es trabajo (decisión D3):
          // ni página ni recuento en la portada, y sin ninguna línea que lo
          // diga. Se salta ANTES de `fetchActionRecord` (~10 consultas). Las
          // comunicaciones y los accidentes anulados siguen saliendo: D3
          // habla solo de actuaciones.
          rec = isWorkState(childOrder.state)
            ? await fetchActionRecord(childId, cloudName)
            : null;
        } else if (childType === OrderType.Accident) {
          rec = await fetchAccidentRecord(childId, cloudName);
        } else if (childType === OrderType.Communication) {
          rec = await fetchCommunicationRecord(childId);
        } else {
          console.warn(
            '[combined-report] child order de tipo inesperado, se omite:',
            childType,
            childId
          );
        }
        completed++;
        onProgress?.(completed, childOrderIds.length);
        return rec
          ? {
              record: rec,
              registerInitDate: childOrder?.registerInitDate ?? null,
              creationDate: childOrder?.creationDate ?? null,
            }
          : null;
      })
    );
    for (const child of chunkRecords) {
      if (child) children.push(child);
    }
  }

  return children;
}

/**
 * Construye los datos de un informe combinado para una Generic Order o Campaña:
 * la Order padre + sus hijas (actuaciones, accidentes, comunicaciones), menos
 * las actuaciones que no son trabajo (borrador o anulada, decisión D3).
 *
 * Las hijas cuelgan del padre vía `order.childExpeditionOrderIDs` (ids de Order
 * hijas). Cada id se pasa directamente a los builders existentes, que ya aceptan
 * el id de la Order y resuelven la entidad correspondiente.
 */
export async function fetchCombinedReportData(params: {
  entityId: string;
  entityType: CombinedEntityType;
  cloudName: string;
  /** Orden de las hijas elegido en el modal (default: registro + desc). */
  sort?: ReportSortOption;
  onProgress?: (current: number, total: number) => void;
}): Promise<CombinedReportData | null> {
  const { entityId, entityType, cloudName, sort, onProgress } = params;

  // El Recorregut de Vigilància NO pasa por aquí: tiene documento propio
  // (`SurveillanceReport` + `generateSurveillancePDF`, 2026-07-27) porque su
  // tabla de tramos necesita página horizontal y sus contadores de accidentes
  // y comunicaciones eran siempre 0 (matriz D0: un RV solo admite Actuacions
  // y tramos).

  // 1. La fila seleccionada en los históricos YA es el id de la Order padre
  //    (selectAllOrders devuelve `id: order.id`, no el id de la entidad
  //    GenericOrder/Campaign). Por eso usamos entityId como parentOrderId directo.
  const isCampaign = entityType === 'campaign';
  // Del servidor primero: de aquí sale la lista de hijas
  // (`childExpeditionOrderIDs`), y una caché vieja dejaría fuera las nuevas.
  // Sin red, la de la caché (ver `readOrderPreferServer`).
  const orderRes = await readOrderPreferServer(entityId);
  const order = orderRes?.order;
  if (!order) return null;

  // Nombre de campaña (opcional): la Order padre no lo tiene; se resuelve desde
  // la entidad Campaign emparejada (order.campaignIDs[0]).
  let campaignName: string | undefined;
  if (isCampaign && order.campaignIDs?.[0]) {
    const campRes = await safeQuery<{ campaign: any }>(GET_CAMPAIGN_BY_ID, {
      id: order.campaignIDs[0],
    });
    campaignName = campRes?.campaign?.name;
  }

  // 2. Datos de la Order padre (info básica + imágenes propias)
  const [
    locationFields,
    classificationFields,
    creatorFields,
    teamFields,
    parentImages,
  ] = await Promise.all([
    resolveLocationFields(order),
    resolveClassificationFields(order),
    resolveCreatorFields(order.userId),
    resolveTeamFields(order.teamIDs ?? []),
    fetchImages(order.imageIDs ?? [], cloudName),
  ]);

  const roid = formatRoid(order.roid);

  // Operario que registra (Order.registeredByUserId → nombre), mismo helper
  // cache-first compartido con los informes de Actuación/Accidente.
  const registeredByName = await resolveRegisteredByName(order.registeredByUserId);

  const parentBasicInfo: { label: string; value: string }[] = [
    { label: 'ROID', value: roid },
    // Solo las campañas tienen nombre; en las OT genéricas `campaignName` es
    // undefined y el campo sencillamente no se pinta (antes se resolvía y se
    // descartaba: el nombre de la campaña no llegaba al informe).
    ...(campaignName
      ? [{ label: t('campaigns:name', 'Nom de la campanya'), value: campaignName }]
      : []),
    {
      label: t('tables:headers.status', 'Estat'),
      value: translateState(order.state ?? ''),
    },
    ...(order.registerInitDate
      ? [
          {
            label: t('forms:workOrder.startDate', 'Data registre'),
            value: formatDate(order.registerInitDate),
          },
        ]
      : []),
    ...(order.derivedEndDate
      ? [
          {
            label: t('forms:workOrder.endDate', 'Data final'),
            value: formatDate(order.derivedEndDate),
          },
        ]
      : []),
    ...creatorFields,
    ...teamFields,
    ...(order.registeredByUserId
      ? [{ label: t('forms:registeredBy.label', 'Registrat per'), value: registeredByName }]
      : []),
    ...classificationFields,
    ...locationFields,
  ];

  // 3. Hijas por childExpeditionOrderIDs (troceado), dispatch por tipo. Se
  //    acumulan en UNA sola lista mixta: el orden final es un hilo cronológico
  //    entrelazado, no bloques por tipo.
  const children = await fetchChildrenWithDates(
    order.childExpeditionOrderIDs ?? [],
    cloudName,
    onProgress
  );

  // 4. Secuencia final de hijas: UN único hilo cronológico ENTRELAZADO con los
  //    3 tipos mezclados, por la fecha/dirección del modal (default: fecha de
  //    registro descendente). Fechas null siempre al final. El entrelazado es
  //    por informe: cada Order padre ordena SUS hijas.
  const { field, direction } = sort ?? DEFAULT_REPORT_SORT;
  const sortedChildren = buildReportChildrenSequence(children, field, direction);

  return {
    title: `${t('modals:downloadHistoric.combinedTitle')} ${roid}`,
    generatedAt: formatGeneratedAt(),
    entityType,
    parent: {
      roid,
      state: order.state ?? '',
      basicInfo: parentBasicInfo,
      description: order.description ?? '',
      notes: order.notes ?? undefined,
      images: parentImages,
    },
    children: sortedChildren,
    counts: {
      actions: sortedChildren.filter((r) => r.type === 'action').length,
      accidents: sortedChildren.filter((r) => r.type === 'accident').length,
      communications: sortedChildren.filter((r) => r.type === 'communication')
        .length,
    },
  };
}
